import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import {
  IconSingle,
  IconTwoCols,
  IconTwoRows,
  IconThreeCols,
  IconThreeRows,
  IconThreeTopSplit,
  IconThreeBottomSplit,
  IconFourGrid,
  IconFourCols,
  IconFourLeftMain,
  IconFourTopMain,
  getLayoutIcon,
} from "./LayoutIcons";

const LAYOUT_GROUPS = [
  {
    count: 1,
    options: [{ id: "1", title: "Single Chart", icon: IconSingle, slots: 1 }],
  },
  {
    count: 2,
    options: [
      { id: "2-cols", title: "2 Columns (Side by Side)", icon: IconTwoCols, slots: 2 },
      { id: "2-rows", title: "2 Rows (Stacked)", icon: IconTwoRows, slots: 2 },
    ],
  },
  {
    count: 3,
    options: [
      { id: "3-cols", title: "3 Columns", icon: IconThreeCols, slots: 3 },
      { id: "3-rows", title: "3 Rows", icon: IconThreeRows, slots: 3 },
      { id: "3-top-split", title: "1 Top / 2 Bottom", icon: IconThreeTopSplit, slots: 3 },
      { id: "3-bottom-split", title: "2 Top / 1 Bottom", icon: IconThreeBottomSplit, slots: 3 },
    ],
  },
  {
    count: 4,
    options: [
      { id: "4-grid", title: "2x2 Grid", icon: IconFourGrid, slots: 4 },
      { id: "4-cols", title: "4 Columns", icon: IconFourCols, slots: 4 },
      { id: "4-left-main", title: "1 Left / 3 Right", icon: IconFourLeftMain, slots: 4 },
      { id: "4-top-main", title: "1 Top / 3 Bottom", icon: IconFourTopMain, slots: 4 },
    ],
  },
];

const MultiChartSelector = ({ currentLayout = "1", onSelectLayout, btnColor: propBtnColor }) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const handleSelect = (layoutId) => {
    onSelectLayout(layoutId);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Trigger Button */}
      <button
        ref={btnRef}
        type="button"
        title="Multi Chart Layout"
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          borderRadius: "6px",
          border: open
            ? `1px solid ${themeBtnColor}`
            : currentLayout !== "1"
              ? `1px solid ${themeBtnColor}`
              : "1px solid rgba(125, 125, 125, 0.2)",
          backgroundColor:
            open || currentLayout !== "1"
              ? alpha(themeBtnColor, 0.12)
              : "transparent",
          color: currentLayout !== "1" ? themeBtnColor : "var(--text-color)",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: 600,
          transition: "all 0.15s ease",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          if (!open && currentLayout === "1") {
            e.currentTarget.style.borderColor = alpha(themeBtnColor, 0.5);
          }
        }}
        onMouseLeave={(e) => {
          if (!open && currentLayout === "1") {
            e.currentTarget.style.borderColor = "rgba(125, 125, 125, 0.2)";
          }
        }}
      >
        {getLayoutIcon(currentLayout, currentLayout !== "1", themeBtnColor)}
        <span style={{ fontSize: "11px", opacity: 0.85 }}>
          {currentLayout === "1" ? "Multi Chart" : currentLayout}
        </span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Binance-style Multi Chart Dropdown */}
      {open && (
        <div
          ref={menuRef}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1200,
            width: "280px",
            backgroundColor: "var(--body-color-less, #1e2329)",
            border: "1px solid rgba(125, 125, 125, 0.2)",
            borderRadius: "10px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.45)",
            padding: "12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            backdropFilter: "blur(10px)",
            userSelect: "none",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
              paddingBottom: "8px",
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "var(--text-color, #eaecef)",
                letterSpacing: "0.02em",
              }}
            >
              Multi Chart
            </span>
            <span
              style={{
                fontSize: "11px",
                color: themeBtnColor,
                fontWeight: 600,
              }}
            >
              Layouts
            </span>
          </div>

          {/* Rows 1, 2, 3, 4 */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {LAYOUT_GROUPS.map((group) => (
              <div
                key={group.count}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                {/* Row number label (1, 2, 3, 4) */}
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "rgba(255, 255, 255, 0.4)",
                    width: "12px",
                    textAlign: "center",
                  }}
                >
                  {group.count}
                </span>

                {/* Option icons */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {group.options.map((opt) => {
                    const isSelected = currentLayout === opt.id;
                    const IconComp = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        title={opt.title}
                        onClick={() => handleSelect(opt.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "5px 7px",
                          borderRadius: "6px",
                          border: isSelected
                            ? `1.5px solid ${themeBtnColor}`
                            : "1px solid rgba(125, 125, 125, 0.2)",
                          backgroundColor: isSelected
                            ? alpha(themeBtnColor, 0.15)
                            : "rgba(255, 255, 255, 0.04)",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          outline: "none",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = alpha(themeBtnColor, 0.5);
                            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = "rgba(125, 125, 125, 0.2)";
                            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.04)";
                          }
                        }}
                      >
                        <IconComp active={isSelected} color={themeBtnColor} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Quick hint footer */}
          <div
            style={{
              borderTop: "1px solid rgba(125, 125, 125, 0.12)",
              paddingTop: "6px",
              fontSize: "10.5px",
              color: "rgba(255, 255, 255, 0.45)",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Click slot to activate</span>
            <span>Up to 4 charts</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiChartSelector;
